// src/pages/Orders/CreateOrder.jsx
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import orderService from '../../services/order.service';
import dealerService from '../../services/dealer.service';
import Toast from '../../components/Toast/Toast';
import { useAuth } from '../../context/AuthContext';
import './OrderPages.css';

const debounce = (fn, ms = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    return new Promise(resolve => {
      timer = setTimeout(() => resolve(fn(...args)), ms);
    });
  };
};

const CreateOrder = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [toast, setToast]           = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]         = useState({});

  const [formData, setFormData] = useState({
    dealerId: '', godownId: '', notes: '',
  });

  const [items, setItems] = useState([{
    productId: '', orderedUnits: '', orderedBoxes: '', ratePerUnit: '', ratePerBox: '',
    productOption: null,  // store the selected react-select option for label display
  }]);

  // Track selected option labels (so they stay visible after selection)
  const [dealerOption,  setDealerOption]  = useState(null);
  const [godownOption,  setGodownOption]  = useState(null);

  const resolveCompanyId = useCallback(() => {
    if (isSuperAdmin) {
      const cid = localStorage.getItem('selectedCompanyId');
      return cid ? parseInt(cid) : null;
    }
    return user?.companyId || null;
  }, [isSuperAdmin, user]);

  // ── Search loaders (called on every keystroke, debounced) ─────────────────

  const loadDealers = useCallback(async (input) => {
    const cid = resolveCompanyId();
    if (isSuperAdmin && !cid) return [];
    try {
      const res = await dealerService.getAllDealers(cid, 1, 20, input || '');
      const list = res?.data?.dealers || [];
      return list.map(d => ({
        value: d.id,
        label: `${d.dealerShopName} — ${d.ownerName}`,
      }));
    } catch (err) {
      console.error('Dealer search error:', err);
      return [];
    }
  }, [isSuperAdmin, resolveCompanyId]);

  const loadGodowns = useCallback(async (input) => {
    try {
      const res = await orderService.getGodowns(1, 20, input || '');
      const list = res?.data?.godowns || [];
      return list.map(g => ({
        value: g.id,
        label: `${g.name} — ${g.location}`,
      }));
    } catch (err) {
      console.error('Godown search error:', err);
      return [];
    }
  }, []);

  const loadProducts = useCallback(async (input) => {
  const cid = resolveCompanyId();
  if (isSuperAdmin && !cid) return [];
  try {
    const res = await orderService.getProducts(cid, 1, 1000, input || '');   // ← 1000
    const list = res?.data?.products || [];
    return list.map(p => ({
      value: p.id,
      label: `${p.productName}${p.productAndPacking ? ` (${p.productAndPacking})` : ''}`,
      product: p,
    }));
  } catch (err) {
    console.error('Product search error:', err);
    return [];
  }
}, [isSuperAdmin, resolveCompanyId]);

  // Debounced versions (one per loader so they don't share a timer)
  const debouncedLoadDealers  = useCallback(debounce(loadDealers, 300),  [loadDealers]);
  const debouncedLoadGodowns  = useCallback(debounce(loadGodowns, 300),  [loadGodowns]);
  const debouncedLoadProducts = useCallback(debounce(loadProducts, 300), [loadProducts]);

  // ── Field handlers ────────────────────────────────────────────────────────

  const handleNotesChange = (e) => {
    setFormData(prev => ({ ...prev, notes: e.target.value }));
  };

  const handleDealerSelect = (option) => {
    setDealerOption(option);
    setFormData(prev => ({ ...prev, dealerId: option ? option.value : '' }));
    if (errors.dealerId) setErrors(prev => ({ ...prev, dealerId: '' }));
  };

  const handleGodownSelect = (option) => {
    setGodownOption(option);
    setFormData(prev => ({ ...prev, godownId: option ? option.value : '' }));
    if (errors.godownId) setErrors(prev => ({ ...prev, godownId: '' }));
  };

  const handleProductSelect = (index, option) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        productId:    option ? option.value : '',
        productOption: option,
        // Auto-fill rates from the full product payload we stashed in option.product
        ratePerUnit:  option?.product?.ratePerUnit ?? updated[index].ratePerUnit,
        ratePerBox:   option?.product?.ratePerBox  ?? updated[index].ratePerBox,
      };
      return updated;
    });
    const key = `item_${index}_product`;
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleItemFieldChange = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => setItems(prev => [...prev, {
    productId: '', orderedUnits: '', orderedBoxes: '',
    ratePerUnit: '', ratePerBox: '', productOption: null,
  }]);

  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  const lineTotal = (item) => {
    const units = parseFloat(item.orderedUnits || 0) * parseFloat(item.ratePerUnit || 0);
    const boxes = parseFloat(item.orderedBoxes || 0) * parseFloat(item.ratePerBox  || 0);
    return units + boxes;
  };

  const orderTotal = items.reduce((sum, item) => sum + lineTotal(item), 0);

  const validate = () => {
    const newErrors = {};
    if (!formData.dealerId) newErrors.dealerId = 'Dealer is required';
    if (!formData.godownId) newErrors.godownId = 'Godown is required';
    items.forEach((item, i) => {
      if (!item.productId) newErrors[`item_${i}_product`] = 'Product is required';
      if (!item.orderedUnits && !item.orderedBoxes)
        newErrors[`item_${i}_qty`] = 'Enter units or boxes quantity';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        dealerId: parseInt(formData.dealerId),
        godownId: parseInt(formData.godownId),
        notes:    formData.notes || undefined,
        items: items.map(item => ({
          productId:    parseInt(item.productId),
          orderedUnits: parseInt(item.orderedUnits || 0),
          orderedBoxes: parseInt(item.orderedBoxes || 0),
          ratePerUnit:  parseFloat(item.ratePerUnit),
          ratePerBox:   parseFloat(item.ratePerBox),
        })),
      };

      const res = await orderService.createOrder(payload);
      if (res.success) {
        setToast({
          message: res.data?.orderId
            ? `Order ${res.data.orderId} created successfully!`
            : 'Order created successfully!',
          type: 'success',
        });
        setTimeout(() => navigate('/orders/my-orders'), 1500);
      }
    } catch (error) {
      setToast({
        message: error.response?.data?.message || 'Failed to create order',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Shared AsyncSelect styles (matches your existing UI)
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu:       (base) => ({ ...base, maxHeight: 240, overflowY: 'auto' }),
    control:    (base) => ({ ...base, minHeight: 42 }),
  };

  return (
    <div className="order-page-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav className="order-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span><span>Orders</span><span>›</span><span>Create Order</span>
      </nav>

      <div className="order-header">
        <h4 className="order-title">Create Order</h4>
        <p className="order-subtitle">Place a new order for a dealer</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="order-form-card">
          <p className="order-section-title">Order Details</p>

          <div className="order-form-row">
            <div className="order-form-group">
              <label className="order-label">Dealer <span className="req">*</span></label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={debouncedLoadDealers}
                value={dealerOption}
                onChange={handleDealerSelect}
                placeholder="Type to search dealer..."
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'No dealers match your search' : 'Start typing to search'}
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
              {errors.dealerId && <span className="order-error">{errors.dealerId}</span>}
            </div>

            <div className="order-form-group">
              <label className="order-label">Godown <span className="req">*</span></label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={debouncedLoadGodowns}
                value={godownOption}
                onChange={handleGodownSelect}
                placeholder="Type to search godown..."
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'No godowns match your search' : 'Start typing to search'}
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
              {errors.godownId && <span className="order-error">{errors.godownId}</span>}
            </div>
          </div>

          <div className="order-form-row">
            <div className="order-form-group order-full-width">
              <label className="order-label">Notes</label>
              <textarea
                name="notes" className="order-input"
                value={formData.notes} onChange={handleNotesChange}
                placeholder="Additional notes (optional)" rows={2}
              />
            </div>
          </div>
        </div>

        <div className="order-form-card" style={{ marginTop: 20 }}>
          <p className="order-section-title">Products</p>

          {items.map((item, index) => (
            <div key={index} className="order-item-row">
              <div className="order-item-header">
                <span className="order-item-num">Item #{index + 1}</span>
                {index > 0 && (
                  <button type="button" className="order-remove-btn" onClick={() => removeItem(index)}>
                    ✕ Remove
                  </button>
                )}
              </div>

              <div className="order-form-row">
                <div className="order-form-group" style={{ flex: 2 }}>
                  <label className="order-label">Product <span className="req">*</span></label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={debouncedLoadProducts}
                    value={item.productOption}
                    onChange={(option) => handleProductSelect(index, option)}
                    placeholder="Type to search product..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? 'No products match your search' : 'Start typing to search'}
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                  {errors[`item_${index}_product`] && (
                    <span className="order-error">{errors[`item_${index}_product`]}</span>
                  )}
                </div>

                <div className="order-form-group">
                  <label className="order-label">Units Qty</label>
                  <input type="number" className="order-input" value={item.orderedUnits}
                    onChange={(e) => handleItemFieldChange(index, 'orderedUnits', e.target.value)}
                    placeholder="0" min="0"/>
                </div>

                <div className="order-form-group">
                  <label className="order-label">Box Qty</label>
                  <input type="number" className="order-input" value={item.orderedBoxes}
                    onChange={(e) => handleItemFieldChange(index, 'orderedBoxes', e.target.value)}
                    placeholder="0" min="0"/>
                </div>

                <div className="order-form-group">
                  <label className="order-label">Rate/Unit (₹)</label>
                  <input type="number" className="order-input" value={item.ratePerUnit}
                    onChange={(e) => handleItemFieldChange(index, 'ratePerUnit', e.target.value)}
                    placeholder="0.00" step="0.01"/>
                </div>

                <div className="order-form-group">
                  <label className="order-label">Rate/Box (₹)</label>
                  <input type="number" className="order-input" value={item.ratePerBox}
                    onChange={(e) => handleItemFieldChange(index, 'ratePerBox', e.target.value)}
                    placeholder="0.00" step="0.01"/>
                </div>

                <div className="order-form-group">
                  <label className="order-label">Line Total</label>
                  <div className="order-line-total">
                    ₹{lineTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {errors[`item_${index}_qty`] && (
                <span className="order-error" style={{ marginTop: -10, display: 'block' }}>
                  {errors[`item_${index}_qty`]}
                </span>
              )}
            </div>
          ))}

          <button type="button" className="order-add-item-btn" onClick={addItem}>
            + Add Product
          </button>

          <div className="order-total-row">
            <span className="order-total-label">Order Total</span>
            <span className="order-total-value">
              ₹{orderTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        <div className="order-actions">
          <button type="button" className="order-btn-secondary"
            onClick={() => navigate('/orders/my-orders')} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="order-btn-primary" disabled={submitting}>
            {submitting ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrder;